import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaPaperPlane } from "react-icons/fa";
import { toast } from "react-toastify";

export default function PacsShareChatCard({
  chatConnection = {},
  language = "en",
  shareLinkReady = false,
  onSendTemplate,
  canSendInvite = false,
}) {
  const { t } = useTranslation("common");
  const isRTL = language === "he" || language === "ar";
  const messages = useMemo(
    () => chatConnection.messages || [],
    [chatConnection.messages],
  );
  const sendMessage = chatConnection.sendMessage;
  const hasConversation = chatConnection.hasConversation || false;
  const connectedNumber = chatConnection.connectedNumber || "";
  const canSendFreeFormMessage = chatConnection.canSendFreeFormMessage || false;
  const hasTemplateSent = chatConnection.hasTemplateSent || false;
  const hasPatientResponded = chatConnection.hasPatientResponded || false;

  const [inputText, setInputText] = useState("");
  const messagesRef = useRef(null);

  useEffect(() => {
    const el = messagesRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages]);

  const handleSend = useCallback(async () => {
    const text = (inputText || "").trim();
    if (!text) return;
    if (!canSendFreeFormMessage) {
      if (!hasTemplateSent) {
        toast.warning(
          t("chatSendTemplateFirst") || "Please send a template message first.",
        );
        return;
      }
      if (!hasPatientResponded) {
        toast.info(
          t("chatWaitingForResponse") || "Waiting for patient response.",
        );
        return;
      }
    }
    setInputText("");
    try {
      await sendMessage?.(text);
    } catch (err) {
      if (err.requiresTemplate || err.message?.includes("24-hour")) {
        toast.error(
          t("chat24HourWindowExpired") ||
            "24-hour window expired. Please send a template first.",
        );
      } else {
        toast.error(t("chatSendFailed"));
      }
    }
  }, [
    inputText,
    canSendFreeFormMessage,
    hasTemplateSent,
    hasPatientResponded,
    sendMessage,
    t,
  ]);

  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
      <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/80">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
          {t("pacsShareChatWindow") || "Chat Window"}
        </h3>
        <span className="text-xs font-medium text-gray-500 dark:text-gray-400 truncate">
          {t("pacsShareChatTarget") || "Target"}: {connectedNumber || "—"}
        </span>
      </div>

      <div className="flex items-center justify-between gap-2 px-4 py-2 border-b border-gray-100 dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400">
        <span>
          {shareLinkReady
            ? t("pacsShareChatReadyHint") || "Link is ready. Send the template, then message here."
            : t("pacsShareChatWaitHint") ||
              "Chat starts after the secure study link is ready."}
        </span>
        {onSendTemplate ? (
          <button
            type="button"
            onClick={onSendTemplate}
            disabled={!shareLinkReady || !canSendInvite}
            className="shrink-0 h-8 px-3 rounded-lg bg-[#A4C639] dark:bg-[#CBEA7B] text-gray-900 dark:text-black font-semibold disabled:opacity-50"
          >
            {t("pacsShareSendTemplate") || "Send Template"}
          </button>
        ) : null}
      </div>

      <div
        ref={messagesRef}
        className={`h-40 overflow-y-auto p-3 bg-white dark:bg-gray-800 ${
          messages.length === 0
            ? "flex items-center justify-center"
            : "space-y-2"
        }`}
      >
        {messages.length === 0 ? (
          <p className="text-xs text-gray-400 dark:text-gray-500 text-center px-2">
            {t("pacsShareChatEmpty") ||
              "No message sent yet. Create the share link, send the template, then chat here."}
          </p>
        ) : (
          messages.map((msg) => (
            <div
              key={msg.sid}
              className={`flex ${msg.isFromRadiologist ? "justify-end" : "justify-start"}`}
            >
              <div
                className={`max-w-[85%] rounded-lg px-2.5 py-1.5 text-xs ${
                  msg.isFromRadiologist
                    ? "bg-[#CBEA7B] dark:bg-[#A4C63980] text-gray-900 dark:text-gray-100"
                    : "bg-gray-100 dark:bg-gray-600 text-gray-900 dark:text-gray-100"
                }`}
                dir={isRTL ? "rtl" : "ltr"}
              >
                <div className="whitespace-pre-wrap break-all [overflow-wrap:anywhere]" dir="auto">
                  {msg.body}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      <div className="flex gap-2 p-3 border-t border-gray-200 dark:border-gray-700">
        <input
          type="text"
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
          placeholder={
            !shareLinkReady
              ? t("pacsShareChatWaitHint") || "Waiting for share link..."
              : !hasTemplateSent
                ? t("chatPlaceholderSendTemplate") || "Send a template first..."
                : !hasPatientResponded
                  ? t("chatPlaceholderWaiting") || "Waiting for response..."
                  : t("chatPlaceholder")
          }
          disabled={!hasConversation || !canSendFreeFormMessage}
          className="flex-1 min-w-0 h-9 rounded-lg border dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 px-3 text-xs focus:outline-none focus:ring-2 focus:ring-[#A4C639] dark:focus:ring-[#CBEA7B] disabled:opacity-60"
          dir={isRTL ? "rtl" : "ltr"}
        />
        <button
          type="button"
          onClick={handleSend}
          disabled={
            !hasConversation || !inputText.trim() || !canSendFreeFormMessage
          }
          className="h-9 px-3 rounded-lg bg-[#A4C639] dark:bg-[#CBEA7B] text-gray-900 dark:text-black disabled:opacity-50 inline-flex items-center gap-1.5 text-xs font-semibold"
        >
          {t("chatSend")}
          <FaPaperPlane size={12} />
        </button>
      </div>
    </div>
  );
}
